import { useTranslation } from 'react-i18next';

// Task status and permission utilities
export const getEffectiveStatus = (task) => {
  if (!task) return '';
  
  if (task.effective_status) {
    return task.effective_status;
  }
  
  if (task.status === 'FORWARDED' && task.metadata?.forwardedCompletion) {
    return task.metadata.forwardedCompletion.status || task.status;
  }
  
  return task.status;
};

export const isTaskInProgress = (task) => {
  if (!task) return false;
  const effectiveStatus = getEffectiveStatus(task);
  return ['ACCEPTED', 'IN_PROGRESS'].includes(effectiveStatus) ||
    (effectiveStatus === 'ASSIGNED' && task.accepted_at);
};

export const isInformationalOnly = (task) => {
  if (!task || !task.metadata) return false;
  let metaObj = task.metadata;
  if (typeof metaObj === 'string') {
    try { 
      metaObj = JSON.parse(metaObj); 
    } catch(e) { 
      return false; 
    }
  }
  return !!metaObj.informational_only;
};

export const getAssigneeId = (task) => {
  if (!task) return null;
  return task.assignee?.id ?? task.assignee_id ?? null;
};

export const isCurrentUserAssignee = (task, user) => {
  return !!user && getAssigneeId(task) === user.id;
};

// Permission checkers
export const canMarkAsReceived = (task, user) => {
  if (isInformationalOnly(task)) return false;
  return isCurrentUserAssignee(task, user) && task?.status === 'ASSIGNED';
};

export const canProcessDocument = (task, user) => {
  if (!isCurrentUserAssignee(task, user) || !task) return false;
  if (isInformationalOnly(task)) return ['ASSIGNED', 'IN_PROGRESS'].includes(task.status);
  return ['IN_PROGRESS', 'RECEIVED'].includes(task.status);
};

export const canRejectDocument = (task, user) => {
  if (isInformationalOnly(task)) return false;
  return isCurrentUserAssignee(task, user) && task && ['IN_PROGRESS', 'RECEIVED'].includes(task.status);
};

export const canForwardDocument = (task, user) => {
  if (isInformationalOnly(task)) return false;
  return isCurrentUserAssignee(task, user) && task && ['IN_PROGRESS', 'RECEIVED'].includes(task.status);
};

// Translation utilities
export const useTaskTranslations = () => {
  const { t, i18n } = useTranslation(['tasks', 'documents', 'common']);
  
  const getTaskStatusLabel = (status) => {
    if (!status) return '-';
    
    const currentLang = i18n.language;
    
    // Direct translation for Pashto
    if (currentLang === 'ps') {
      const pashtoLabels = {
        'COMPLETED': 'بشپړ شوی',
        'IN_PROGRESS': 'د کار په حال کې',
        'ASSIGNED': 'ګمارل شوی',
        'REJECTED': 'رد شوی',
        'FORWARDED': 'لېږل شوی'
      };
      return pashtoLabels[status] || status;
    }
    
    try {
      const result = t(`tasks:${status}`);
      return typeof result === 'object' ? status : result;
    } catch (error) {
      console.error('Translation error for status:', status, error);
      return status;
    }
  };

  const getDocTypeLabel = (type) => {
    if (!type) return '-';
    try {
      const typeMap = {
        'letter': t('documents:type_letter', 'Letter'),
        'memo': t('documents:type_memo', 'Memo'),
        'report': t('documents:type_report', 'Report'),
        'proposal': t('documents:type_proposal', 'Proposal'),
        'request': t('documents:type_request', 'Request'),
        'approval': t('documents:type_approval', 'Approval'),
        'contract': t('documents:type_contract', 'Contract'),
        'financial': t('documents:type_financial', 'Financial'),
        'legal': t('documents:type_legal', 'Legal'),
        'other': t('documents:type_other', 'Other')
      };
      return typeMap[type.toLowerCase()] || t(`documents:${type.toLowerCase()}`, type);
    } catch (error) {
      console.warn(`Error getting type label for: ${type}`, error);
      return type || '-';
    }
  };

  const getDocStatusLabel = (status) => {
    if (!status) return '-';
    try {
      const statusMap = {
        'DRAFT': t('documents:status_draft', 'Draft'),
        'SUBMITTED': t('documents:status_submitted', 'Submitted'),
        'IN_REVIEW': t('documents:status_in_review', 'In Review'),
        'APPROVED': t('documents:status_approved', 'Approved'),
        'REJECTED': t('documents:status_rejected', 'Rejected'),
        'COMPLETED': t('documents:status_completed', 'Completed'),
        'CANCELLED': t('documents:status_cancelled', 'Cancelled')
      };
      return statusMap[status.toUpperCase()] || status;
    } catch (error) {
      console.warn(`Error getting status label for: ${status}`, error);
      return status || '-';
    }
  };

  return {
    getTaskStatusLabel,
    getDocTypeLabel,
    getDocStatusLabel,
    t
  };
};

// UI utilities for activity timeline
export const getActivityIcon = (event) => {
  const isCompletionEvent = 
    event.displayType === 'COMPLETED' || 
    event.type === 'COMPLETED' || 
    event.type === 'WORKFLOW_STEP_COMPLETED' ||
    (event.type === 'STATUS_CHANGED' && 
     (event.action === 'TASK_COMPLETED' || 
      event.metadata?.status === 'COMPLETED'));

  if (isCompletionEvent) return 'CheckCircle';
  
  const iconMap = {
    'ASSIGNED': 'Assignment',
    'REJECTED': 'Cancel',
    'RECEIPT_REJECTED': 'Cancel',
    'RECEIVED': 'Done',
    'FORWARDED': 'ForwardToInbox',
    'STATUS_CHANGED': event.action === 'TASK_COMPLETED' ? 'CheckCircle' : 
                     event.action === 'TASK_ACCEPTED' ? 'ThumbUp' : 'History'
  };
  
  return iconMap[event.type] || 'History';
};

export const getActivityTypeText = (event, t) => {
  const isCompletionEvent = 
    event.displayType === 'COMPLETED' || 
    event.type === 'COMPLETED' || 
    event.type === 'WORKFLOW_STEP_COMPLETED' ||
    (event.type === 'STATUS_CHANGED' && 
     (event.action === 'TASK_COMPLETED' || 
      event.metadata?.status === 'COMPLETED'));

  if (isCompletionEvent || event.action === 'TASK_COMPLETED') {
    return t('tasks:task_completed');
  }
  
  // Handle specific action types
  if (event.action === 'DOCUMENT_RECEIVED') {
    return t('tasks:document_received', 'Document Received');
  }
  
  if (event.action === 'TASK_REJECTED') {
    return t('tasks:task_rejected');
  }
  
  const typeMap = {
    'ASSIGNED': t('tasks:task_assigned'),
    'RECEIPT_REJECTED': t('tasks:task_rejected'),
    'RECEIVED': t('tasks:document_received', 'Document Received'),
    'REJECTED': t('tasks:task_rejected'),
    'FORWARDED': t('tasks:task_forwarded'),
    'STATUS_CHANGED': event.metadata?.status === 'REJECTED' || event.action === 'TASK_REJECTED' 
      ? t('tasks:task_rejected') 
      : t('tasks:task_status_changed', 'د سندونه حالت بدل شو')
  };
  
  return typeMap[event.type] || t(`tasks:${event.type?.toLowerCase()}`);
};

export const getActivityColors = (event, theme) => {
  const colorMap = {
    'RECEIVED': { bg: '#c8e6c9', border: '#81c784' }, // Light green for physically received
    'RECEIPT_REJECTED': { bg: theme.palette.error.light, border: theme.palette.error.main },
    'FORWARDED': { bg: theme.palette.warning.light, border: theme.palette.warning.main },
    'COMPLETED': { bg: theme.palette.success.light, border: theme.palette.success.main }, // Standard green for completed
    'REJECTED': { bg: theme.palette.error.light, border: theme.palette.error.main }
  };
  
  if (event.displayType === 'COMPLETED' || event.action === 'TASK_COMPLETED') {
    return colorMap['COMPLETED'];
  }
  
  return colorMap[event.type] || { bg: theme.palette.primary.light, border: theme.palette.primary.main };
}; 